import React, { useEffect, useMemo } from "react";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table-v3";
import { createTheme, ThemeProvider } from "@mui/material";
import { useDarkMode } from "../../components/theme-toggler/theme-context";
import { tableTheme } from "./theme";
import { commonTableProps } from "./props";

/**
 * DataTable — shared MRT v3 table with built-in theming.
 *
 * Reads ThemeContext internally so callers don't need to wire ThemeProvider,
 * createTheme, or theming logic themselves.
 *
 * Props:
 *   columns     MRT v3 column definitions array (required)
 *   data        row data array (required)
 *   tableProps  any additional useMaterialReactTable options; spread last so
 *               they override commonTableProps defaults (e.g. renderDetailPanel,
 *               renderRowActions, renderTopToolbarCustomActions, state, etc.)
 *   tableRef    optional ref to access the table instance (for row selection,
 *               expansion, etc.)
 */
export default function DataTable({ columns, data, tableProps = {}, tableRef }) {
  const dark = useDarkMode();

  const memoTheme = useMemo(() => createTheme(tableTheme(dark)), [dark]);

  // Filter out any columns that lack an id or accessorKey
  const filteredColumns = useMemo(() => {
    if (!columns) return [{id:"empty",accessorKey:"empty",accessorFn:()=>{return "."}}];
    // Retain columns that have a defined 'id' or 'accessorKey'
    return columns.filter(
      (col) => col && (col.id !== undefined && col.id !== null && col.id !== "" || col.accessorKey !== undefined && col.accessorKey !== null && col.accessorKey !== "")
    );
  }, [columns]);

  const table = useMaterialReactTable({
    columns: filteredColumns,
    data,
    ...commonTableProps,
    ...tableProps,
  });
  
  useEffect(() => {
    if (tableRef) {
      tableRef.current = table;
    }
  }, [table, tableRef]);

  return (
    <ThemeProvider theme={memoTheme}>
      <MaterialReactTable table={table} />
    </ThemeProvider>
  );
}
